import type { ObjectId } from "mongodb";
import type { Editable, CSSSettings, EditableWithOverwrites, Overwrite, OverwriteMediaItem } from "hoxton-types";
import { EditableImageTypes, EditableMediaTypes } from "hoxton-shared/enums/editables";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";

import addGroupValueMutation from "@/apollo/mutations/v2/AddEditableGroupValue.gql";
import updateGroupValuesMutation from "@/apollo/mutations/v2/UpdateEditableGroupValues.gql";
import removeGroupValuesMutation from "@/apollo/mutations/v2/RemoveEditableGroupValues.gql";
import removeOverwritesMutation from "@/apollo/mutations/RemoveOverwrites.gql";
import overwriteEditablesWithEditableGroupAndMasterTemplateScope from "@/apollo/mutations/v2/OverwriteEditablesWithEditableGroupAndMasterTemplateScope.gql";
import overwriteEditablesWithEditableGroupScope from "@/apollo/mutations/v2/OverwriteEditablesWithEditableGroupScope.gql";
import overwriteEditablesWithMasterTemplateScope from "@/apollo/mutations/v2/OverwriteEditablesWithMasterTemplateScope.gql";
import overwriteEditablesWithCampaignScope from "@/apollo/mutations/v2/OverwriteEditablesWithCampaignScope.gql";

import { apolloClient } from "@/apollo";

type EditableOverwrites = {
    overwrite: Omit<Overwrite, "_id">;
    editable: Editable;
};

type OverwritesByScope = Record<string, EditableOverwrites[]>;

function stripId<T extends { _id: string | ObjectId }>({ _id, ...props }: T) {
    return props;
}

// EditableOverwrite
type OverwriteToSave = {
    editableId: string;
    language: string;
    masterTemplateId?: (string | ObjectId) | null;
    editableGroupValueIds?: (string | ObjectId)[];
    resizeSettings?: OverwriteMediaItem["resizeSettings"] | null;
    mediaId?: string | null;
    value?: Overwrite["value"] | null;
    settings?: CSSSettings & { fontFamily?: string };
};

export function getOverwritesToSave(overwrites: OverwritesByScope[string]) {
    return overwrites.reduce<OverwriteToSave[]>((acc, { editable, overwrite }) => {
        // Filter out overwrites with empty editableGroupValueIds
        if (
            (overwrite.scope === OverwriteScope.EditableGroup ||
                overwrite.scope === OverwriteScope.EditableGroupTemplate) &&
            (!overwrite.editableGroupValueIds || overwrite.editableGroupValueIds.length === 0)
        ) {
            return acc;
        }

        const overwriteToSave: OverwriteToSave = {
            editableId: editable._id,
            language: overwrite.language
        };

        switch (overwrite.scope) {
            case OverwriteScope.EditableGroup:
                overwriteToSave.editableGroupValueIds = overwrite.editableGroupValueIds;

                break;
            case OverwriteScope.EditableGroupTemplate:
                overwriteToSave.masterTemplateId = overwrite.masterTemplateId;
                overwriteToSave.editableGroupValueIds = overwrite.editableGroupValueIds;
                break;
            default:
                break;
        }

        if (EditableImageTypes.includes(editable.type as (typeof EditableImageTypes)[number]) && overwrite.mediaItem) {
            overwriteToSave.resizeSettings = overwrite.mediaItem.resizeSettings ?? null;
        }

        if (EditableMediaTypes.includes(editable.type as (typeof EditableImageTypes)[number])) {
            overwriteToSave.mediaId = overwrite.value as string;
        } else {
            overwriteToSave.value = overwrite.value;
        }

        if (overwrite.settings) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            overwriteToSave.settings = { ...overwrite.settings };

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            if (overwriteToSave.settings.fontFamily) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                overwriteToSave.settings.fontFamily = overwriteToSave.settings.fontFamily._id;
            }
        }

        acc.push(overwriteToSave);
        return acc;
    }, []);
}

export function getOverwriteMutation(scope: string) {
    switch (scope) {
        case OverwriteScope.Campaign:
            return overwriteEditablesWithCampaignScope;

        case OverwriteScope.Template:
            return overwriteEditablesWithMasterTemplateScope;

        case OverwriteScope.EditableGroup:
            return overwriteEditablesWithEditableGroupScope;

        case OverwriteScope.EditableGroupTemplate:
            return overwriteEditablesWithEditableGroupAndMasterTemplateScope;
        default:
            return overwriteEditablesWithEditableGroupAndMasterTemplateScope;
    }
}

type GqlQuery = {
    mutation: string;
};

export default class Editables {
    $apollo: typeof apolloClient;
    refreshQuery: GqlQuery | null;
    context: null;
    defaults: {
        refetchQueries?: (GqlQuery | null)[];
        variables: { campaignId: string };
    };

    constructor(
        apollo: typeof apolloClient,
        campaignId: string,
        {
            refreshQuery = null,
            context = null
        }: {
            refreshQuery?: GqlQuery | null;
            context?: null;
        } = {}
    ) {
        this.$apollo = apollo;
        this.refreshQuery = refreshQuery;
        this.context = context;

        this.defaults = {
            variables: {
                campaignId
            }
        };

        if (refreshQuery) {
            this.defaults.refetchQueries = [this.refreshQuery];
        }
    }

    getContext() {
        return this.context ? { context: this.context } : {};
    }

    addEditableGroupValue(editableGroupName: string, value: unknown) {
        return this.$apollo.mutate({
            mutation: addGroupValueMutation,
            variables: {
                editableGroupName,
                value,
                campaignId: this.defaults.variables.campaignId
            },
            ...this.getContext()
        });
    }

    removeEditableGroupValues(editableGroupValueIds: string[]) {
        return this.$apollo.mutate({
            mutation: removeGroupValuesMutation,
            variables: { editableGroupValueIds },
            ...this.getContext()
        });
    }

    async updateEditableGroupValues(editableGroupValues: unknown) {
        return this.$apollo.mutate({
            mutation: updateGroupValuesMutation,
            variables: { editableGroupValues },
            ...this.getContext()
        });
    }

    async removeOverwrites(overwriteIds: string[]) {
        if (!overwriteIds.length) {
            return null;
        }

        return this.$apollo.mutate({
            mutation: removeOverwritesMutation,
            variables: {
                campaignId: this.defaults.variables.campaignId,
                overwriteIds
            },
            ...this.getContext()
        });
    }

    async addOverwrites(editableOverwrites: EditableWithOverwrites[]) {
        const overwritesByScope = {} as OverwritesByScope;

        editableOverwrites.forEach(editableOverwrite => {
            if (!editableOverwrite.overwrites?.length) {
                return;
            }

            editableOverwrite.overwrites.forEach(overwrite => {
                overwritesByScope[overwrite.scope] = overwritesByScope[overwrite.scope] || [];
                overwritesByScope[overwrite.scope].push({
                    editable: editableOverwrite.editable,
                    overwrite: stripId(overwrite)
                });
            });
        });

        const mutations = Object.keys(overwritesByScope).map(scope =>
            this.$apollo.mutate({
                mutation: getOverwriteMutation(scope),
                variables: {
                    campaignId: this.defaults.variables.campaignId,
                    overwrites: getOverwritesToSave(overwritesByScope[scope])
                },
                ...this.getContext()
            })
        );

        return Promise.all(mutations);
    }
}
